import React, { useState, useEffect } from "react";
import { useLicense } from "../../hooks/useLicense";
import api from "../../services/api";
import { toast } from "react-toastify";

export default function InviteTeamModal({ open, onClose, onInvited }) {
  const { companyHasOctopushX, companyHasRecordingStudio, seats } =
    useLicense();

  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [grantX, setGrantX] = useState(false);
  const [grantRS, setGrantRS] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Sensible defaults: preselect whichever products the company has and has seats for
  useEffect(() => {
    if (!open) return;
    setEmail("");
    setRole("member");
    setGrantX(companyHasOctopushX && seats.octopushX?.available > 0);
    setGrantRS(
      companyHasRecordingStudio && seats.recordingStudio?.available > 0,
    );
  }, [open, companyHasOctopushX, companyHasRecordingStudio, seats]);

  if (!open) return null;

  const xDisabled =
    !companyHasOctopushX || (seats.octopushX && seats.octopushX.available <= 0);
  const rsDisabled =
    !companyHasRecordingStudio ||
    (seats.recordingStudio && seats.recordingStudio.available <= 0);

  const canSubmit = email && (grantX || grantRS) && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.inviteTeamMember(email, role, {
        octopushX: grantX,
        recordingStudio: grantRS,
      });
      toast.success("Invitation sent");
      onInvited?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send invitation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#0C0E12] rounded-lg p-6 w-full max-w-lg border border-gray-700">
        <h3 className="text-lg font-medium text-white mb-1">
          Invite Team Member
        </h3>
        <p className="text-gray-400 text-sm mb-5">
          Choose the products this person should be able to access.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 text-sm mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
              placeholder="name@company.com"
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-300 text-sm mb-2">
              Product access
            </label>

            <div className="space-y-2">
              <label
                className={`flex items-center justify-between bg-[#13161B] border border-gray-700 rounded-lg px-3 py-3 ${
                  xDisabled ? "opacity-50" : "cursor-pointer"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    disabled={xDisabled}
                    checked={grantX}
                    onChange={(e) => setGrantX(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600"
                  />
                  <div>
                    <div className="text-white text-sm font-medium">
                      Octopush X
                    </div>
                    <div className="text-xs text-gray-400">
                      {companyHasOctopushX
                        ? `${seats.octopushX.available} of ${seats.octopushX.max} seats available`
                        : "Company does not have this license"}
                    </div>
                  </div>
                </div>
              </label>

              <label
                className={`flex items-center justify-between bg-[#13161B] border border-gray-700 rounded-lg px-3 py-3 ${
                  rsDisabled ? "opacity-50" : "cursor-pointer"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    disabled={rsDisabled}
                    checked={grantRS}
                    onChange={(e) => setGrantRS(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600"
                  />
                  <div>
                    <div className="text-white text-sm font-medium">
                      Recording Studio
                    </div>
                    <div className="text-xs text-gray-400">
                      {companyHasRecordingStudio
                        ? `${seats.recordingStudio.available} of ${seats.recordingStudio.max} seats available`
                        : "Company does not have this license"}
                    </div>
                  </div>
                </div>
              </label>
            </div>

            {!grantX && !grantRS && (
              <p className="text-xs text-amber-400 mt-2">
                Pick at least one product for the invitee.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Sending…" : "Send Invitation"}
          </button>
        </div>
      </div>
    </div>
  );
}
